import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Chip, IconButton, Paper, Typography, TextField, Tooltip, Button,
  Menu, MenuItem, ListItemText, CircularProgress, Divider,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCommentIcon from '@mui/icons-material/AddComment';
import type { SessionSummary } from '../agent/sessionApi';
import { ERROR_ACTION_LABELS } from '../agent/errorRecovery';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AgentMessage from './AgentMessage';
import AgentActivityCard from './AgentActivityCard';
import AgentConfirmCard from './AgentConfirmCard';
import AgentUpdateConfirmCard from './AgentUpdateConfirmCard';
import AgentPlanCard from './AgentPlanCard';
import AgentWizardForm from './AgentWizardForm';
import AgentFeedbackDialog from './AgentFeedbackDialog';
import AgentMailDraftCard from './AgentMailDraftCard';
import { getWorkflow } from '../agent/mailWorkflows';
import AgentWizardInput from './AgentWizardInput';
import type { UseAgentReturn } from '../hooks/useAgent';
import { isPhoneUserAgent } from '../lib/deviceDetect';

// スマホ・タブレットでは入力欄の Enter を改行にする（送信はボタン）。PC は従来どおり Enter 送信 / Shift+Enter 改行。
// 主な入力がタッチ（pointer: coarse）かで判定するので、タッチ画面付きノート PC は PC 扱いのまま
const enterInsertsNewline = isPhoneUserAgent()
  || (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches);

interface Props {
  agent: UseAgentReturn;
  onClose: () => void;
  /** モバイル等で全画面表示したい場合に true。位置・サイズが画面いっぱいに切り替わる */
  fullscreen?: boolean;
  /** フロート位置（left/top）。null/未指定なら既定の右上固定（§16-4 S1） */
  floatPos?: { x: number; y: number } | null;
  /** ヘッダをドラッグハンドルにするための props（AgentFloat が渡す） */
  dragHandleProps?: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: () => void;
    style?: React.CSSProperties;
  };
}

export default function AgentPanel({ agent, onClose, fullscreen = false, floatPos = null, dragHandleProps }: Props) {
  const { uiMessages, status, currentStep, wizard, send, resetConversation, resolveConfirm, answerWizard, answerWizardAll, sendFeedback, handleErrorAction,
    listRecentSessions, loadSession, activeSessionKey } = agent;
  const [input, setInput] = useState('');
  // セッション一覧（3日分・§16 S2）。メニューを開いたときだけ取得する
  const [sessionMenuAnchor, setSessionMenuAnchor] = useState<HTMLElement | null>(null);
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null);
  const openSessionMenu = useCallback(async (el: HTMLElement) => {
    setSessionMenuAnchor(el);
    setSessions(null);
    try {
      setSessions(await listRecentSessions(3));
    } catch {
      setSessions([]);  // 取得失敗はメニュー内に「取得できません」と出す
    }
  }, [listRecentSessions]);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [uiMessages, currentStep, scrollToBottom]);

  const canSend = status === 'idle' || status === 'error';
  const isAwaitingConfirm = status === 'awaiting_confirm';
  const isAwaitingWizard = status === 'awaiting_wizard' && !!wizard;
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [feedbackBusy, setFeedbackBusy] = React.useState(false);
  // 予約作成の詳細入力: 既定は1枚のフォーム。「1問ずつ」を選んだ人にはそれを覚えておく（端末ごと）
  const [wizardMode, setWizardMode] = React.useState<'form' | 'step'>(() => {
    try { return localStorage.getItem('agent.wizardMode') === 'step' ? 'step' : 'form'; } catch { return 'form'; }
  });
  const chooseWizardMode = (m: 'form' | 'step') => {
    setWizardMode(m);
    try { localStorage.setItem('agent.wizardMode', m); } catch { /* 保存できなくても動く */ }
  };

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    await send(text);
  }, [input, send]);

  // 表示モードに応じた Paper の sx を構築
  const paperSx = fullscreen
    ? {
        position: 'fixed' as const,
        inset: 0,
        width: '100vw',
        height: '100vh',
        // iOS の動的 viewport / セーフエリア対応
        // dvh が使えるブラウザではそちらを優先
        ['@supports (height: 100dvh)']: { height: '100dvh' },
        display: 'flex' as const,
        flexDirection: 'column' as const,
        zIndex: 10500,
        borderRadius: 0,
        overflow: 'hidden' as const,
        // iPhone のセーフエリアぶんは内側の Box が padding で吸収する
      }
    : {
        position: 'fixed' as const,
        // フロート位置が保存されていればそこへ。無ければ従来の右上
        ...(floatPos ? { left: floatPos.x, top: floatPos.y } : { right: 16, top: 80 }),
        width: 420,
        maxWidth: 'calc(100vw - 32px)',
        height: '70vh',
        minHeight: 500,
        maxHeight: 'calc(100vh - 100px)',
        display: 'flex' as const,
        flexDirection: 'column' as const,
        zIndex: 10500,
        borderRadius: 2,
        overflow: 'hidden' as const,
        border: '1px solid #e0e7ff',
      };

  return (
    <Paper elevation={10} sx={paperSx}>
      {/* ヘッダー（フロート時はドラッグハンドル） */}
      <Box
        {...(dragHandleProps || {})}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          userSelect: 'none',
          ...(dragHandleProps ? { cursor: 'grab' } : {}),
          // フルスクリーン表示時は iPhone 等のセーフエリア（ノッチ）ぶん上端に余白
          ...(fullscreen && { pt: 'calc(8px + env(safe-area-inset-top, 0px))' }),
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: '#fff',
        }}
      >
        <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          <SmartToyIcon sx={{ fontSize: 20, flexShrink: 0 }} />
          {agent.sessionMeta.kind === 'reservation' ? (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1, opacity: 0.8 }}>
                業務 ▸ 予約セッション
              </Typography>
              <Typography variant="subtitle2" fontWeight={800} noWrap>
                {agent.sessionMeta.label || agent.sessionMeta.anchor}
              </Typography>
            </Box>
          ) : (
            <Typography variant="subtitle1" fontWeight={800}>
              エージェント
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={0.5}>
          {agent.sessionMeta.kind === 'reservation' && (
            <Tooltip title="業務セッションへ戻る">
              <IconButton size="small" onClick={agent.returnToWorkSession} sx={{ color: '#fff' }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="セッション（3日分）">
            <IconButton size="small" onClick={(e) => void openSessionMenu(e.currentTarget)} sx={{ color: '#fff' }}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="新規会話">
            <IconButton size="small" onClick={resetConversation} sx={{ color: '#fff' }}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={dragHandleProps ? '最小化（●になります）' : '閉じる'}>
            <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* メッセージリスト */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          background: '#fafafa',
          py: 1,
        }}
      >
        {uiMessages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <SmartToyIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              何か手伝えることはありますか？
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              例: 「田中花子さんを教えて」<br />
              「7/10 10:00 - 7/12 18:00、アルファードで予約して」
            </Typography>
          </Box>
        )}

        {uiMessages.map((m) => {
          if (m.kind === 'user') return <AgentMessage key={m.id} role="user" text={m.text} />;
          if (m.kind === 'assistant') return <AgentMessage key={m.id} role="assistant" text={m.text} />;
          if (m.kind === 'error') {
            return (
              <Box key={m.id}>
                <AgentMessage role="error" text={m.text} />
                {m.actions && m.actions.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, px: 2, pb: 1, flexWrap: 'wrap' }}>
                    {m.actions.map((a) => (
                      <Button
                        key={a}
                        size="small"
                        variant="outlined"
                        color={a === 'reset' ? 'error' : 'primary'}
                        onClick={() => handleErrorAction(a)}
                        disabled={status === 'thinking' || status === 'calling_tool'}
                        sx={{ fontSize: '0.72rem', py: 0.2 }}
                      >
                        {ERROR_ACTION_LABELS[a]}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            );
          }
          if (m.kind === 'activity') {
            return (
              <AgentActivityCard
                key={m.id}
                toolName={m.toolName}
                args={m.args}
                status={m.status}
                result={m.result}
                errorMessage={m.errorMessage}
                durationMs={m.durationMs}
              />
            );
          }
          if (m.kind === 'confirm') {
            return (
              <AgentConfirmCard
                key={m.id}
                args={m.args}
                state={m.state}
                onResolve={m.state === 'pending' ? resolveConfirm : undefined}
              />
            );
          }
          if (m.kind === 'mail_draft') {
            return (
              <AgentMailDraftCard
                key={m.id}
                to={m.to}
                subject={m.subject}
                body={m.body}
                replyMode={m.replyMode}
                state={m.state}
                note={m.note}
                busy={status === 'calling_tool' || status === 'thinking'}
                onSend={(body) => void agent.sendMailDraft(m.id, body)}
                onDiscard={() => agent.discardMailDraft(m.id)}
                onEditSave={(body) => agent.editMailDraft(m.id, body)}
              />
            );
          }
          if (m.kind === 'plan') {
            return (
              <AgentPlanCard
                key={m.id}
                title={m.title}
                steps={m.steps}
                state={m.state}
                onResolve={m.state === 'pending' ? resolveConfirm : undefined}
              />
            );
          }
          if (m.kind === 'update_confirm') {
            return (
              <AgentUpdateConfirmCard
                key={m.id}
                before={m.before}
                changes={m.changes}
                state={m.state}
                onResolve={m.state === 'pending' ? resolveConfirm : undefined}
              />
            );
          }
          return null;
        })}

        {currentStep && !isAwaitingConfirm && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {currentStep}
            </Typography>
          </Box>
        )}
      </Box>

      <AgentFeedbackDialog
        open={feedbackOpen}
        busy={feedbackBusy}
        onClose={() => setFeedbackOpen(false)}
        onSubmit={async (category, text) => {
          setFeedbackBusy(true);
          try { await sendFeedback(category, text); } finally { setFeedbackBusy(false); setFeedbackOpen(false); }
        }}
      />
      {/* 業務セッションでもフィードバックは送れる（予約セッションは下の行にまとめて出す） */}
      {agent.sessionMeta.kind !== 'reservation' && canSend && !isAwaitingWizard && uiMessages.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.6, px: 1, py: 0.6, borderTop: '1px solid #eef2f7', background: '#fff' }}>
          <Chip
            size="small" label="📝 フィードバックを送る" onClick={() => setFeedbackOpen(true)}
            sx={{ fontSize: 12, cursor: 'pointer', bgcolor: '#fff7ed', border: '1px solid #fed7aa', '&:hover': { bgcolor: '#ffedd5' } }}
          />
        </Box>
      )}

      {/* クイックアクション（予約セッション中・待機時のみ。2026-08-30 前田さん要望:
          「新着確認」ボタンでメール等をワンタップ確認 — 特にスマホで打鍵を減らす） */}
      {agent.sessionMeta.kind === 'reservation' && canSend && !isAwaitingWizard && (
        <Box sx={{ display: 'flex', gap: 0.6, px: 1, py: 0.6, borderTop: '1px solid #eef2f7', background: '#fff', flexWrap: 'wrap' }}>
          {/* メール種別の標準手順チップ（§16-6y）。押すと決まった指示文をそのまま送る */}
          {getWorkflow(agent.sessionMeta.workflow)?.steps.map((st) => (
            <Chip
              key={st.label}
              size="small"
              label={st.plan ? `📝 ${st.label}` : st.label}
              onClick={() => void send(st.instruction)}
              sx={{ fontSize: 12, cursor: 'pointer', bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', '&:hover': { bgcolor: '#d1fae5' } }}
            />
          ))}
          <Chip
            size="small"
            label="📬 新着確認"
            onClick={() => void send('この予約の新着メールを確認してください。前回確認より新しい受信があれば、内容を要約して対応が必要か教えてください。')}
            sx={{ fontSize: 12, cursor: 'pointer', bgcolor: '#eef2ff', '&:hover': { bgcolor: '#e0e7ff' } }}
          />
          <Chip
            size="small"
            label="🕘 経緯を確認"
            onClick={() => void send('この予約のこれまでの経緯（メール・操作・メモ）を時系列で簡潔にまとめてください。')}
            sx={{ fontSize: 12, cursor: 'pointer', bgcolor: '#eef2ff', '&:hover': { bgcolor: '#e0e7ff' } }}
          />
          <Chip
            size="small" label="📝 フィードバックを送る" onClick={() => setFeedbackOpen(true)}
            sx={{ fontSize: 12, cursor: 'pointer', bgcolor: '#fff7ed', border: '1px solid #fed7aa', '&:hover': { bgcolor: '#ffedd5' } }}
          />
        </Box>
      )}

      {/* 入力欄（ウィザード時はウィザード入力に置換） */}
      {isAwaitingWizard && wizard ? (
        <Box sx={fullscreen ? { pb: 'env(safe-area-inset-bottom, 0px)' } : undefined}>
          {wizardMode === 'form'
            ? <AgentWizardForm wizard={wizard} onSubmit={answerWizardAll} onOneByOne={() => chooseWizardMode('step')} />
            : <AgentWizardInput wizard={wizard} onAnswer={answerWizard} />}
        </Box>
      ) : (
        <Box sx={{
          borderTop: '1px solid #e2e8f0',
          p: 1,
          background: '#fff',
          // フルスクリーン表示時は下端のホームバー領域ぶん余白
          ...(fullscreen && { pb: 'calc(8px + env(safe-area-inset-bottom, 0px))' }),
        }}>
          <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder={
                isAwaitingConfirm
                  ? '確認カードのボタンで応答してください…'
                  : canSend
                    ? 'メッセージを入力…'
                    : '応答中…'
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                // IME変換中の Enter は送信しない（日本語変換の確定を誤爆しない）
                const ne: any = e.nativeEvent;
                if (ne?.isComposing || ne?.keyCode === 229) return;
                // スマホ・タブレット（タッチ端末）は Enter＝改行、送信は送信ボタンだけ（2026-09-26）。
                // ソフトキーボードには Shift+Enter が無く、改行が打てなかった
                if (enterInsertsNewline) return;
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={!canSend || isAwaitingConfirm}
              // iOS Safari は input の font-size が 16px 未満だとフォーカス時に自動ズームする。
              // PC とモバイルで共通コンポーネントを保ちたいので、両者とも 16px に揃える。
              inputProps={{ style: { fontSize: 16 }, enterKeyHint: enterInsertsNewline ? 'enter' : 'send' }}
            />
            <IconButton
              color="primary"
              disabled={!canSend || !input.trim() || isAwaitingConfirm}
              onClick={handleSend}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      )}
      {/* セッション一覧（3日分）。データは消えない — 3日は表示の絞り込み（§16-2 決定2） */}
      <Menu
        anchorEl={sessionMenuAnchor}
        open={!!sessionMenuAnchor}
        onClose={() => setSessionMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 420, width: 320 } } }}
      >
        <MenuItem
          onClick={() => { setSessionMenuAnchor(null); resetConversation(); }}
          sx={{ color: 'primary.main', fontWeight: 700 }}
        >
          <AddCommentIcon fontSize="small" sx={{ mr: 1 }} /> 新規セッション
        </MenuItem>
        <Divider />
        {sessions === null && (
          <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} /> 読み込み中…</MenuItem>
        )}
        {sessions !== null && sessions.length === 0 && (
          <MenuItem disabled>直近3日のセッションはありません</MenuItem>
        )}
        {(sessions || []).map((sess) => (
          <MenuItem
            key={sess.id}
            selected={sess.client_key === activeSessionKey}
            onClick={async () => {
              setSessionMenuAnchor(null);
              if (sess.client_key === activeSessionKey) return;
              await loadSession(sess.id);
            }}
          >
            <ListItemText
              primary={sess.title || '(無題)'}
              secondary={`${sess.operator || '—'} ・ ${formatSessionTime(sess.last_active_at)} ・ ${sess.message_count}件`}
              primaryTypographyProps={{ noWrap: true, fontSize: '0.85rem' }}
              secondaryTypographyProps={{ fontSize: '0.72rem' }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Paper>
  );
}

/** 一覧の時刻表示: 今日は時刻だけ、それ以外は月/日 時刻 */
function formatSessionTime(s: string): string {
  const d = new Date(String(s).replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return sameDay ? hm : `${d.getMonth() + 1}/${d.getDate()} ${hm}`;
}
